import React from "react";
import { MessageCircle, Phone, Check, Truck, Clock, Shield } from "lucide-react";
import { COMPANY, waLink, telLink } from "../lib/contact";

const QUICKS = [
  "Atendimento rápido",
  "Caçambas para obras e reformas",
  "Melhor custo-benefício da região",
  "Atendimento residencial e empresarial",
  "Entrega rápida em Jundiaí e Região",
];

export const Hero = () => {
  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative pt-24 md:pt-28 pb-12 md:pb-20 overflow-hidden bg-white"
    >
      {/* Decorative bg grid */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      {/* Big yellow blob */}
      <div
        aria-hidden
        className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#fff505] blur-2xl opacity-60 hidden md:block"
      />

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-6 items-center">
          {/* Left content */}
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0f172a] text-[#fff505] text-xs font-extrabold uppercase tracking-[0.2em] mb-6">
              <span className="w-2 h-2 bg-[#fff505] inline-block animate-pulse" />
              Disk Entulho · Jundiaí e Região
            </div>

            <h1
              data-testid="hero-title"
              className="font-display text-[44px] sm:text-6xl md:text-7xl lg:text-[88px] text-[#0f172a]"
            >
              Caçambas de{" "}
              <span className="relative inline-block">
                <span className="relative z-10">entulho</span>
                <span
                  aria-hidden
                  className="absolute left-0 right-0 bottom-1 h-3 md:h-5 bg-[#fff505] -z-0"
                />
              </span>{" "}
              em <span className="text-[#027ec3]">Jundiaí</span> com atendimento rápido
            </h1>

            <p className="mt-6 text-base md:text-lg text-[#475569] max-w-2xl font-medium leading-relaxed">
              Aluguel de caçambas para obras, reformas, limpezas e remoção de
              entulho em <strong className="text-[#0f172a]">Jundiaí e toda a Região</strong>.
              Atendimento imediato, preço justo e entrega rápida na sua porta.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={waLink("Olá! Quero solicitar uma caçamba para entulho. Pode me passar valores e disponibilidade?")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                className="group inline-flex items-center justify-center gap-3 px-6 md:px-8 py-4 md:py-5 bg-[#25d366] text-white font-extrabold uppercase tracking-wide border-2 border-[#0f172a] shadow-[6px_6px_0_#0f172a] hover:shadow-[10px_10px_0_#0f172a] hover:-translate-x-[3px] hover:-translate-y-[3px] transition-all text-base md:text-lg"
              >
                <MessageCircle className="w-6 h-6" strokeWidth={2.5} />
                Solicitar caçamba no WhatsApp
              </a>
              <a
                href={telLink}
                data-testid="hero-phone-btn"
                className="inline-flex items-center justify-center gap-3 px-6 md:px-8 py-4 md:py-5 bg-[#fff505] text-[#0f172a] font-extrabold uppercase tracking-wide border-2 border-[#0f172a] shadow-[6px_6px_0_#0f172a] hover:shadow-[10px_10px_0_#0f172a] hover:-translate-x-[3px] hover:-translate-y-[3px] transition-all text-base md:text-lg"
              >
                <Phone className="w-6 h-6" strokeWidth={2.5} />
                Ligar agora
              </a>
            </div>

            <ul className="mt-8 grid sm:grid-cols-2 gap-2.5 max-w-xl">
              {QUICKS.map((q) => (
                <li
                  key={q}
                  className="flex items-start gap-2.5 text-sm md:text-[15px] font-semibold text-[#0f172a]"
                >
                  <span className="mt-0.5 w-5 h-5 flex-shrink-0 bg-[#fff505] border-2 border-[#0f172a] flex items-center justify-center">
                    <Check className="w-3.5 h-3.5" strokeWidth={4} />
                  </span>
                  {q}
                </li>
              ))}
            </ul>

            {/* Stat strip */}
            <div className="mt-10 grid grid-cols-3 gap-3 max-w-2xl">
              {[
                { icon: Truck, n: "+10", l: "Anos de estrada" },
                { icon: Clock, n: "60min", l: "Entrega rápida" },
                { icon: Shield, n: "100%", l: "Descarte legal" },
              ].map(({ icon: Icon, n, l }) => (
                <div
                  key={l}
                  className="border-2 border-[#0f172a] bg-white p-3 md:p-4"
                >
                  <Icon className="w-5 h-5 text-[#027ec3] mb-1" strokeWidth={2.5} />
                  <div className="font-display text-2xl md:text-3xl text-[#0f172a] leading-none">
                    {n}
                  </div>
                  <div className="text-[11px] md:text-xs font-bold uppercase text-[#475569] tracking-wider mt-1">
                    {l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right image */}
          <div className="lg:col-span-5 relative">
            <div className="relative">
              <div
                aria-hidden
                className="absolute inset-0 hazard-stripes-thin -rotate-3 translate-x-3 translate-y-3 -z-10"
                style={{ clipPath: "inset(0 round 0)" }}
              />
              <div className="relative bg-[#fff505] border-2 border-[#0f172a] p-4 md:p-6 shadow-[12px_12px_0_#0f172a]">
                <img
                  src="https://customer-assets.emergentagent.com/job_04c778d5-60fa-4c63-9f95-1580f724c05b/artifacts/wftur075_ChatGPT%20Image%2010%20de%20mai.%20de%202026%2C%2007_25_37.png"
                  alt="Caçamba estacionária amarela da PJ Caçambas para aluguel em Jundiaí"
                  className="w-full h-auto select-none drop-shadow-[0_20px_40px_rgba(0,0,0,0.25)]"
                  loading="eager"
                />
                <div className="absolute -top-4 -left-4 bg-[#0f172a] text-[#fff505] font-display text-base md:text-lg px-4 py-2 -rotate-2 border-2 border-[#fff505]">
                  PJ · Modelo padrão
                </div>
                <div className="absolute -bottom-4 -right-4 bg-[#027ec3] text-white font-extrabold uppercase text-xs md:text-sm px-4 py-2 border-2 border-[#0f172a] tracking-wider">
                  3m³ · 5m³ disponíveis
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee strip */}
      <div className="mt-12 md:mt-20 bg-[#0f172a] border-y-2 border-[#0f172a] overflow-hidden">
        <div className="marquee py-4 text-[#fff505] font-display text-2xl md:text-3xl whitespace-nowrap">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center gap-10 pr-10">
              {[
                "Aluguel de Caçambas",
                "★",
                "Disk Entulho Jundiaí",
                "★",
                "Remoção de Entulho",
                "★",
                "Caçamba para Reforma",
                "★",
                "Atendimento Rápido",
                "★",
                "Caçamba Estacionária",
                "★",
              ].map((t, j) => (
                <span key={j} className="px-4">
                  {t}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
